import { Article, CategoryArticles } from '@wix/answers-api';
import { Box } from '@wix/design-system';
import classNames from 'classnames';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useMemo, useRef } from 'react';

import { categoryMock, categoryMock2 } from '../__mocks__/categories';
import { KnowledgeAgentSearchHero } from '../components/KnowledgeAgentSearchHero';
import { SiteSelectorHero } from '../components/SiteSelectorHero';
import { TrendingArticles } from '../components/TrendingArticles';
import { LOCALES } from '../constants';
import { DATA_HOOKS } from '../dataHooks';
import { useIsSitePickerEnabled } from '../hooks/useIsSitePickerEnabled';
import { getHelpCenterData, getPageSeo } from '../services/answers';
import { fetchBlogs } from '../services/wix-blogs';
import { KbBlog, PageType } from '../types';
import { createLogger, LoggerNamespaces } from '../utils/logger';
import { devFallback, withServerSideProps } from '../utils/with-server-side-props';
import { useStore } from '../zustand/stores';

import css from './HomePage.module.scss';

const TopicsSection = dynamic(
  () =>
    import('../components/TopicsSection').then((mod) => ({
      default: mod.TopicsSection,
    })),
  { ssr: false },
);

const Blogs = dynamic(
  () => import('../components/Blogs').then((mod) => ({ default: mod.Blogs })),
  { ssr: false },
);

const RoadmapBanner = dynamic(
  () =>
    import('../components/RoadmapBanner').then((mod) => ({
      default: mod.RoadmapBanner,
    })),
  { ssr: false },
);

const ExploreMoreResources = dynamic(
  () =>
    import('../components/ExploreMoreResources').then((mod) => ({
      default: mod.ExploreMoreResources,
    })),
  { ssr: false },
);

const MarketplaceEntry = dynamic(
  () =>
    import('../components/MarketplaceEntry').then((mod) => ({
      default: mod.MarketplaceEntry,
    })),
  { ssr: false },
);

const StudioCommunityForumEntry = dynamic(
  () =>
    import('../components/StudioCommunityForumEntry').then((mod) => ({
      default: mod.StudioCommunityForumEntry,
    })),
  { ssr: false },
);

export type HomePageProps = {
  categoriesTopArticles: CategoryArticles[];
  trendingArticles: Article[];
  blogs: KbBlog[];
};

const HomePage: NextPage<HomePageProps> = ({ trendingArticles, blogs }) => {
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree,
  );
  const { locale } = useRouter();
  const { isWixStudioUser } = useStore((state) => state.userSlice);
  const contentRef = useRef(null);

  const isSiteSelector = useIsSitePickerEnabled();

  const scrollIntoViewIfNeeded = () => {
    if (contentRef.current) {
      const focusedItem = contentRef.current as HTMLElement;
      if (focusedItem) {
        const offset = 72;
        const top =
          focusedItem.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  };

  const HeroComponent = useMemo(() => {
    if (isSiteSelector) {
      return <SiteSelectorHero onScrollDown={scrollIntoViewIfNeeded} />;
    }

    return <KnowledgeAgentSearchHero onScrollDown={scrollIntoViewIfNeeded} />;
  }, [isSiteSelector]);

  return (
    <Box
      direction="vertical"
      className={`${css.wrapper} ${css.newHomepage}`}
      dataHook={DATA_HOOKS.HOME_PAGE}
    >
      {HeroComponent}
      <div
        ref={contentRef}
        className={classNames(css.contentWrapper, {
          [css.siteSelector]: isSiteSelector,
        })}
      >
        {!isSiteSelector && <TrendingArticles articles={trendingArticles} />}
        <TopicsSection topics={categoriesTree} />
        <RoadmapBanner />
        {blogs.length > 0 && <Blogs blogs={blogs} />}
        <ExploreMoreResources
          isShowBlogResource={!blogs.length}
          isNonEnglishLocale={locale !== LOCALES.EN}
        />
        {isWixStudioUser ? <StudioCommunityForumEntry /> : <MarketplaceEntry />}
      </div>
    </Box>
  );
};

const BLOGS_TIMEOUT_MS = 3000;

export const getServerSideProps = withServerSideProps(
  async ({ req, res, locale }) => {
    const logger = createLogger(LoggerNamespaces.Server).withAspects(req.aspects);

    const fetchBlogsWithTimeout = async (): Promise<KbBlog[]> => {
      let kbBlogs: KbBlog[] = [];

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('fetchBlogs timeout after 3 seconds')),
          BLOGS_TIMEOUT_MS
        )
      );

      try {
        kbBlogs = await Promise.race([
          fetchBlogs(
            res.locals.appContext,
            req,
            locale,
            res.locals.redisClient,
          ),
          timeoutPromise,
        ]);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        logger.error('fetchBlogs error', {
          locale,
          error: errorMessage,
          errorStack: error instanceof Error ? error.stack : undefined,
        });

      }
      return kbBlogs;
    };

    const [helpCenterData, pageSeo, blogs] = await Promise.all([
      getHelpCenterData(req.aspects, locale, res.locals.redisClient).catch(
        devFallback({ categories: [categoryMock, categoryMock2], articles: [] })
      ),
      getPageSeo(
        req.aspects,
        locale,
        PageType.Homepage,
        res.locals.redisClient,
      ).catch(devFallback(null)),
      fetchBlogsWithTimeout(),
    ]);

    const { categories: categoriesTree, articles: trendingArticles } = helpCenterData;

    return {
      props: {
        pageType: PageType.Homepage,
        categoriesTree,
        pageSeo,
        trendingArticles,
        blogs,
        pageData: {},
      },
    };
  },
);

export default HomePage;
